import React from "react";
import { Link } from "react-router-dom";
import Reveal from "../components/Reveal";
import useSEO from "../hooks/useSEO";

const PIPELINE_STEPS = [
  {
    step: 1,
    title: "Fetch",
    text: "Latest AWS announcements are pulled from RSS feeds every 6 hours",
  },
  {
    step: 2,
    title: "Write",
    text: "Amazon Bedrock AI turns the news into a professional podcast script",
  },
  {
    step: 3,
    title: "Voice",
    text: "Amazon Polly converts the script to natural speech (Matthew voice)",
  },
  {
    step: 4,
    title: "Store",
    text: "Audio files land in S3, metadata in DynamoDB",
  },
  {
    step: 5,
    title: "Serve",
    text: "A React frontend on GitHub Pages delivers episodes to you",
  },
];

const BACKEND_STACK = [
  "AWS Lambda (Node.js 20)",
  "Amazon Bedrock (AI)",
  "Amazon Polly (TTS)",
  "Amazon S3 (Storage)",
  "DynamoDB (Database)",
  "EventBridge (Scheduling)",
  "API Gateway (REST API)",
];

const FRONTEND_STACK = [
  "React 18",
  "React Router",
  "Tailwind CSS",
  "GitHub Pages",
  "GitHub Actions",
];

const LEARNING_GOALS = [
  "Serverless architecture patterns",
  "AI integration with Bedrock",
  "Text-to-speech conversion",
  "Event-driven automation",
  "Modern React development",
  "CI/CD with GitHub Actions",
];

const About = () => {
  useSEO({
    title: "About",
    description:
      "How AWS Daily Updates works: a fully automated RSS-to-podcast pipeline using AWS Lambda, Amazon Bedrock AI, Amazon Polly, S3, and DynamoDB.",
    path: "/about",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "About AWS Daily Updates",
      url: "https://deeppatel0311.github.io/aws-podcast/about",
      description:
        "AWS Daily Updates is a fully automated podcast pipeline: AWS announcements are fetched from RSS feeds, scripted by Amazon Bedrock AI, voiced by Amazon Polly, and served as daily audio episodes.",
    },
  });

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-blue-600 mb-8 transition-colors duration-200 group animate-fade-in"
      >
        <svg
          className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform duration-200"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
        Back to episodes
      </Link>

      {/* Intro */}
      <div className="mb-12 animate-fade-up">
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-ink-900 mb-4">
          About this project
        </h1>
        <p className="text-slate-500 text-base sm:text-lg leading-relaxed max-w-2xl">
          A fully automated RSS-to-podcast pipeline — AWS announcements go in,
          finished audio episodes come out, with no humans in the loop.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-6">
          <Reveal>
          <div className="card p-7 sm:p-8">
            <h2 className="text-xl font-bold text-ink-900 mb-4">What is this?</h2>
            <p className="text-slate-500 leading-relaxed">
              AWS Daily Updates is a podcast platform that automatically converts
              AWS announcements into professional audio content. It demonstrates
              a complete RSS-to-Podcast pipeline built entirely with AWS services
              and AI — from raw news feed to a published episode, with zero
              manual steps.
            </p>
          </div>
          </Reveal>

          <Reveal>
          <div className="card p-7 sm:p-8">
            <h2 className="text-xl font-bold text-ink-900 mb-7">How it works</h2>
            <div className="relative">
              {/* Connecting line */}
              <div className="absolute left-[19px] top-4 bottom-4 w-px bg-slate-200" />
              <div className="space-y-7">
                {PIPELINE_STEPS.map((item) => (
                  <div key={item.step} className="relative flex items-start gap-5">
                    <div className="relative z-10 shrink-0 w-10 h-10 rounded-full bg-ink-900 text-white flex items-center justify-center text-sm font-bold">
                      {item.step}
                    </div>
                    <div className="pt-0.5">
                      <h3 className="font-semibold text-ink-900 mb-1">{item.title}</h3>
                      <p className="text-sm text-slate-500 leading-relaxed">{item.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          </Reveal>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Reveal>
          <div className="card p-6">
            <h3 className="text-base font-bold text-ink-900 mb-5">Tech stack</h3>

            <div className="space-y-5">
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">
                  Backend (AWS)
                </h4>
                <div className="space-y-2">
                  {BACKEND_STACK.map((tech) => (
                    <div key={tech} className="flex items-center gap-2.5 text-sm text-slate-600">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />
                      {tech}
                    </div>
                  ))}
                </div>
              </div>

              <div className="pt-4 border-t border-slate-100">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">
                  Frontend
                </h4>
                <div className="space-y-2">
                  {FRONTEND_STACK.map((tech) => (
                    <div key={tech} className="flex items-center gap-2.5 text-sm text-slate-600">
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-400 shrink-0" />
                      {tech}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          </Reveal>

          <Reveal delay={100}>
          <div className="card p-6">
            <h3 className="text-base font-bold text-ink-900 mb-5">Learning goals</h3>
            <div className="space-y-3">
              {LEARNING_GOALS.map((goal) => (
                <div key={goal} className="flex items-start gap-2.5">
                  <svg
                    className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-sm text-slate-600">{goal}</span>
                </div>
              ))}
            </div>
          </div>
          </Reveal>
        </div>
      </div>

      {/* Bottom banner */}
      <Reveal className="mt-8">
      <div className="card bg-ink-900 border-ink-800 p-8 sm:p-10 text-center">
        <h3 className="text-xl font-bold text-white mb-3">Built for learning</h3>
        <p className="text-slate-300 leading-relaxed max-w-2xl mx-auto text-sm sm:text-base">
          This project is a hands-on exploration of AWS services, AI integration,
          and modern web development — showing how multiple cloud services can
          work together to create a fully automated content pipeline.
        </p>
      </div>
      </Reveal>
    </div>
  );
};

export default About;
